import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { INTERNAL_DESCRIPTION_CATEGORIES, QuickChoiceCategory, QuickChoiceSection } from '../../data/internal-description-categories';
import { INTERNAL_DESCRIPTION_OPTIONS, InternalDescriptionOption } from '../../data/internal-description-options';
import { buildQuickChoiceFinishItem, QuickChoiceFinishItem } from '../../data/internal-description-sentences';

const OTHER_OPTION = 'Other';

export interface QuickChoiceFinishSeed {
  noOfUndercoverCarSpaces: number;
  carAccommodation: string;
}

@Component({
  selector: 'app-internal-description-quick-choice',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './internal-description-quick-choice.component.html',
  styleUrl: './internal-description-quick-choice.component.css',
})
export class InternalDescriptionQuickChoiceComponent implements OnChanges {
  @Input() open = false;
  @Input() title = 'Internal Description';
  @Input() values: Record<string, string> = {};
  @Input() finishSeed: QuickChoiceFinishSeed = { noOfUndercoverCarSpaces: 0, carAccommodation: '' };
  @Output() closed = new EventEmitter<void>();
  @Output() applied = new EventEmitter<Record<string, string>>();
  @Output() copyToImprovements = new EventEmitter<QuickChoiceFinishItem>();

  readonly categories: QuickChoiceCategory[] = INTERNAL_DESCRIPTION_CATEGORIES;
  /** Shown under every room tab; stores to one shared value regardless of the active category (matches tops-web-nz). */
  readonly additionalFeaturesSection: QuickChoiceSection = { label: 'Additional Features', fieldGroup: 'InternalFixtures', mode: 'multi' };

  activeCategoryKey = this.categories[0].key;
  selections: Record<string, string> = {};
  otherText: Record<string, string> = {};
  finishItem: QuickChoiceFinishItem = buildQuickChoiceFinishItem({}, { noOfUndercoverCarSpaces: 0, carAccommodation: '' });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.selections = { ...this.values };
      this.otherText = {};
      this.activeCategoryKey = this.categories[0].key;
      this.finishItem = buildQuickChoiceFinishItem(this.selections, this.finishSeed);
    }
  }

  get activeCategory(): QuickChoiceCategory {
    return this.categories.find((category) => category.key === this.activeCategoryKey) || this.categories[0];
  }

  categoryHasValue(category: QuickChoiceCategory): boolean {
    return category.sections.some((section) => !!this.selections[section.fieldGroup]);
  }

  selectCategory(category: QuickChoiceCategory): void {
    this.activeCategoryKey = category.key;
    if (category.isSummary) this.finishItem = buildQuickChoiceFinishItem(this.selections, this.finishItem);
  }

  visibleSections(category: QuickChoiceCategory): QuickChoiceSection[] {
    return category.sections.filter((section) => this.isSectionVisible(section));
  }

  private isSectionVisible(section: QuickChoiceSection): boolean {
    if (section.showWhenSelected && !this.selections[section.showWhenSelected]) return false;
    if (section.showWhenParameter) {
      const { field, group, parameter } = section.showWhenParameter;
      const value = this.selections[field];
      const option = value ? (INTERNAL_DESCRIPTION_OPTIONS[group] || []).find((item) => item.name.toLowerCase() === value.toLowerCase()) : undefined;
      if (option?.[parameter] !== '1') return false;
    }
    return true;
  }

  optionsFor(section: QuickChoiceSection): InternalDescriptionOption[] {
    return INTERNAL_DESCRIPTION_OPTIONS[section.optionsGroup || section.fieldGroup] || [];
  }

  isSelected(section: QuickChoiceSection, option: InternalDescriptionOption): boolean {
    const value = this.selections[section.fieldGroup] || '';
    if (section.mode === 'single') return value.toLowerCase() === option.name.toLowerCase();
    return value
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .includes(option.name.toLowerCase());
  }

  showOtherText(section: QuickChoiceSection): boolean {
    return this.isSelected(section, { name: OTHER_OPTION });
  }

  toggleOption(section: QuickChoiceSection, option: InternalDescriptionOption): void {
    const key = section.fieldGroup;
    if (section.mode === 'single') {
      this.selections[key] = this.isSelected(section, option) ? '' : option.name;
      if (option.name !== OTHER_OPTION) delete this.otherText[key];
      this.commitSelections();
      return;
    }
    const current = (this.selections[key] || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    const index = current.findIndex((item) => item.toLowerCase() === option.name.toLowerCase());
    if (index >= 0) {
      current.splice(index, 1);
      if (option.name === OTHER_OPTION) delete this.otherText[key];
    } else {
      current.push(option.name);
    }
    // Store in lookup-list order (not click order) so auto-generated sentences read naturally, matching tops-web-nz.
    const listOrder = this.optionsFor(section).map((item) => item.name.toLowerCase());
    current.sort((a, b) => listOrder.indexOf(a.toLowerCase()) - listOrder.indexOf(b.toLowerCase()));
    this.selections[key] = current.join(', ');
    this.commitSelections();
  }

  updateOtherText(section: QuickChoiceSection, text: string): void {
    this.otherText[section.fieldGroup] = text;
    this.commitSelections();
  }

  updateTextField(section: QuickChoiceSection, value: string): void {
    this.selections[section.fieldGroup] = value;
    this.commitSelections();
  }

  updateFinishField<K extends keyof QuickChoiceFinishItem>(field: K, value: QuickChoiceFinishItem[K]): void {
    this.finishItem = { ...this.finishItem, [field]: value };
  }

  close(): void {
    this.commitSelections();
    this.closed.emit();
  }

  /** Writes selections immediately on every tile/text change — there's no explicit "Save" step, matching tops-web-nz. */
  private commitSelections(): void {
    const result: Record<string, string> = { ...this.selections };
    for (const [fieldGroup, text] of Object.entries(this.otherText)) {
      if (!text) continue;
      const current = result[fieldGroup] || '';
      result[fieldGroup] = current
        .split(',')
        .map((item) => (item.trim() === OTHER_OPTION ? text.trim() : item.trim()))
        .filter(Boolean)
        .join(', ');
    }
    this.applied.emit(result);
  }

  copyToImprovementsAndClose(): void {
    this.commitSelections();
    this.copyToImprovements.emit(this.finishItem);
    this.closed.emit();
  }
}
